import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ReportActions } from './report.actions';
import { ReportService } from '@core/services/report.service';
import { HelperService } from '@core/helper/helper.service';

@Injectable()
export class ReportEffects {
  private actions$ = inject(Actions);
  private reportService = inject(ReportService);
  private translateService = inject(TranslateService);
  helperService = inject(HelperService);

  getReports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.loadReport),
      mergeMap((data) =>
        this.reportService.getReports(data).pipe(
          map((data) =>
            data.success
              ? ReportActions.loadReportSuccess(data)
              : ReportActions.loadReportFailure(data)
          ),
          catchError((error) =>
            of(
              ReportActions.loadReportFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  exportExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.exportExcel),
      mergeMap((payload) =>
        this.reportService
          .getReports({
            start_date: payload.start_date?.format('YYYY-MM-DD'),
            end_date: payload.end_date?.format('YYYY-MM-DD'),
            user_id: `${payload.user?.value ?? ''}`,
            ...(payload.master_company_id
              ? { master_company_id: payload.master_company_id }
              : {}),
          })
          .pipe(
            map((data) =>
              data.success
                ? ReportActions.exportExcelSuccess({ ...data, ...payload })
                : ReportActions.exportExcelFailure(data)
            ),
            catchError((error) =>
              of(
                ReportActions.exportExcelFailure({
                  error,
                  success: false,
                  message: this.translateService.instant(
                    'Something went wrong'
                  ),
                })
              )
            )
          )
      )
    )
  );

  exportExcelSuccess = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportActions.exportExcelSuccess),
        tap((data) => {
          if (data.data.length === 0) {
            this.helperService.showErrorToastr(
              this.translateService.instant('There is no data to export.')
            );
            return;
          }

          if (data.start_date && data.end_date)
            this.helperService.exportAsExcelFileReport(
              data.data,
              data.start_date,
              data.end_date,
              data.user?.label ?? 'All'
            );
        })
      ),
    { dispatch: false }
  );

  updateNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.updateNote),
      mergeMap((data) =>
        this.reportService.updateNote(data).pipe(
          map((data) =>
            data.success
              ? ReportActions.updateNoteSuccess(data)
              : ReportActions.updateNoteFailure(data)
          ),
          catchError((error) =>
            of(
              ReportActions.updateNoteFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  getNRTReports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.loadNRTReport),
      mergeMap((data) =>
        this.reportService.getNRTReports(data).pipe(
          map((data) =>
            data.success
              ? ReportActions.loadNRTReportSuccess(data)
              : ReportActions.loadNRTReportFailure(data)
          ),
          catchError((error) =>
            of(
              ReportActions.loadNRTReportFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  exportNRTExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.exportNRTExcel),
      mergeMap((payload) =>
        this.reportService
          .getNRTReports({
            start_date: payload.start_date?.format('YYYY-MM-DD'),
            end_date: payload.end_date?.format('YYYY-MM-DD'),
            user_id: `${payload.user?.value ?? ''}`,
          })
          .pipe(
            map((data) =>
              data.success
                ? ReportActions.exportNRTExcelSuccess({ ...data, ...payload })
                : ReportActions.exportNRTExcelFailure(data)
            ),
            catchError((error) =>
              of(
                ReportActions.exportNRTExcelFailure({
                  error,
                  success: false,
                  message: this.translateService.instant(
                    'Something went wrong'
                  ),
                })
              )
            )
          )
      )
    )
  );

  exportNRTExcelSuccess = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportActions.exportNRTExcelSuccess),
        tap((data) => {
          if (data.data.length === 0) {
            this.helperService.showErrorToastr(
              this.translateService.instant('There is no data to export.')
            );
            return;
          }

          if (data.start_date && data.end_date)
            this.helperService.exportNRTExcelReport(
              data.data,
              data.start_date,
              data.end_date,
              data.user?.label ?? 'All'
            );
        })
      ),
    { dispatch: false }
  );

  getExpenseReports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.loadExpenseReport),
      mergeMap((data) =>
        this.reportService.getExpenseReports(data).pipe(
          map((res) =>
            res.success
              ? ReportActions.loadExpenseReportSuccess(res)
              : ReportActions.loadExpenseReportFailure(res)
          ),
          catchError((error) =>
            of(
              ReportActions.loadExpenseReportFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  exportExpenseExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.exportExpenseExcel),
      mergeMap((payload) =>
        this.reportService
          .getExpenseReports({
            start_date: payload.start_date?.format('YYYY-MM-DD'),
            end_date: payload.end_date?.format('YYYY-MM-DD'),
            user_id: `${payload.user?.value ?? ''}`,
            ...(payload.company_id != null
              ? { company_id: payload.company_id }
              : {}),
          })
          .pipe(
            map((data) =>
              data.success
                ? ReportActions.exportExpenseExcelSuccess({ ...data, ...payload })
                : ReportActions.exportExpenseExcelFailure(data)
            ),
            catchError((error) =>
              of(
                ReportActions.exportExpenseExcelFailure({
                  error,
                  success: false,
                  message: this.translateService.instant(
                    'Something went wrong'
                  ),
                })
              )
            )
          )
      )
    )
  );

  exportExpenseExcelSuccess = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportActions.exportExpenseExcelSuccess),
        tap((data) => {
          if (data.data.length === 0) {
            this.helperService.showErrorToastr(
              this.translateService.instant('There is no data to export.')
            );
            return;
          }

          if (data.start_date && data.end_date)
            this.helperService.exportExpenseExcelReport(
              data.data,
              data.start_date,
              data.end_date,
              data.user?.label ?? 'All'
            );
        })
      ),
    { dispatch: false }
  );

  getWeeklyHoursComparison$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.loadWeeklyHoursComparison),
      mergeMap((data) =>
        this.reportService.getWeeklyHoursComparison(data).pipe(
          map((res) =>
            res.success
              ? ReportActions.loadWeeklyHoursComparisonSuccess(res)
              : ReportActions.loadWeeklyHoursComparisonFailure(res)
          ),
          catchError((error) =>
            of(
              ReportActions.loadWeeklyHoursComparisonFailure({
                error,
                success: false,
                message: this.translateService.instant('Something went wrong'),
              })
            )
          )
        )
      )
    )
  );

  exportWeeklyHoursComparisonExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportActions.exportWeeklyHoursComparisonExcel),
      mergeMap((payload) =>
        this.reportService
          .getWeeklyHoursComparison({
            start_date: payload.start_date?.format('YYYY-MM-DD'),
            end_date: payload.end_date?.format('YYYY-MM-DD'),
            user_id: `${payload.user?.value ?? ''}`,
            ...(payload.company_id != null
              ? { company_id: payload.company_id }
              : {}),
          })
          .pipe(
            map((data) =>
              data.success
                ? ReportActions.exportWeeklyHoursComparisonExcelSuccess({
                    ...data,
                    ...payload,
                  })
                : ReportActions.exportWeeklyHoursComparisonExcelFailure(data)
            ),
            catchError((error) =>
              of(
                ReportActions.exportWeeklyHoursComparisonExcelFailure({
                  error,
                  success: false,
                  message: this.translateService.instant(
                    'Something went wrong'
                  ),
                })
              )
            )
          )
      )
    )
  );

  exportWeeklyHoursComparisonExcelSuccess = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ReportActions.exportWeeklyHoursComparisonExcelSuccess),
        tap((data) => {
          if (data.data.length === 0) {
            this.helperService.showErrorToastr(
              this.translateService.instant('There is no data to export.')
            );
            return;
          }

          if (data.start_date && data.end_date)
            this.helperService.exportComparisonExcelReport(
              data.data,
              data.start_date,
              data.end_date,
              data.user?.label ?? 'All'
            );
        })
      ),
    { dispatch: false }
  );
}
